import { GET_HOME_BANNERS, GET_EXCLUSIVE_BADGE } from './type'

export function getHomeBanners() {
    return (dispatch, getState, {axios}) => {
        // KNOWN DEBT, deliberately left by item 30 (design.md §7.0-D): this returns a
        // `new Promise` in which NEITHER `resolve` NOR `reject` is ever called, so the
        // returned promise never settles — root CLAUDE.md standard 1's unterminated
        // promise. Nothing awaits it today, which is why it is harmless in place.
        // Converting it is out of this item's scope and blast radius; `getExclusiveBadge`
        // below is written the way this one should be.
        return new Promise((resolve, reject) => {
            dispatch({
                type: GET_HOME_BANNERS,
                payload: null
            })
            axios.post('/banner/today')
            .then((response) => {
                dispatch({
                    type: GET_HOME_BANNERS,
                    payload: response.data
                })
            })
            .catch((error) => {
                dispatch({
                    type: GET_HOME_BANNERS,
                    payload: false
                })
            })
        })
    }
    
}

/**
 * Milliseconds the badge fetch is allowed to take before the render gives up on it.
 *
 * Deliberately short. This call sits on the SSR critical path for every page, and the
 * cost of losing it is nil — the components fall back to the bundled PNG, which is
 * byte-identical to the default uploaded image. A slow backend must never hold a page
 * open; `src/actions/layout/index.js:90-93` uses the same setTimeout-resolves idiom
 * with a 10 s budget, which would be far too long here.
 */
export const EXCLUSIVE_BADGE_TIMEOUT_MS = 1000;

/**
 * Fetch the admin-configured "Only at BEAUTRIUM" badge image URL.
 *
 * Dispatches `GET_EXCLUSIVE_BADGE` with the URL string, or with `null` on every
 * failure path. `null` means "render the bundled asset", which is what the three
 * render sites do with `badgeUrl || exclusiveImg`.
 *
 * INVARIANT — THIS PROMISE MUST NEVER REJECT (design.md §7.0-D, qc-design.md M2).
 * `server/loader.js` awaits it inside `coreFrontload()` (`:487`, the `await` at `:495`),
 * and neither of the two dispatch branches that call it (`:465` authenticated, `:478-479`
 * anonymous) has a `.catch()`. A
 * rejection there means the SSR response never completes: the request hangs and a
 * worker leaks on EVERY page load until the backend recovers. A backend returning 500
 * in 80 ms reaches that well inside the timeout below. So: no `throw` in the catch, no
 * un-wrapped await, and the timeout resolves rather than rejects.
 *
 * @returns {Function} a redux-thunk taking `(dispatch, getState, { axNL })`.
 *   `axNL` — the background client, so this never shows the global spinner.
 *   Resolves to `{ imageUrl: string|null }`; never rejects.
 */
export function getExclusiveBadge() {
    return async (dispatch, getState, { axNL }) => {
        /**
         * Settle once, whatever happens first — the request or the timeout.
         * @param {string|null} imageUrl
         * @returns {{ imageUrl: string|null }}
         */
        const finish = (imageUrl) => {
            dispatch({ type: GET_EXCLUSIVE_BADGE, payload: imageUrl });
            return { imageUrl };
        };

        let timeoutId = null;
        const timeout = new Promise((resolve) => {
            // Resolves, never rejects — see the invariant above.
            timeoutId = setTimeout(() => resolve(null), EXCLUSIVE_BADGE_TIMEOUT_MS);
        });

        const request = (async () => {
            try {
                const { data } = await axNL.get('/v3/web-config/exclusive-badge');
                // Defensive on the body shape as well as on the status: a malformed
                // payload (`{}`, or `image_url: 123`) must degrade to the bundled asset,
                // not put a number into an <img src>.
                const url = data && typeof data.image_url === 'string' ? data.image_url.trim() : '';
                return url || null;
            } catch (err) {
                // 404 (backend not deployed yet), 500, 503 (identity blip, or the
                // migration not applied), network error and malformed body all land
                // here and all mean the same thing to the renderer: use the bundled PNG.
                // Not logged with the error object — an axios rejection carries
                // `error.config.headers`, which holds the token on an authenticated
                // render (root CLAUDE.md rule 4).
                return null;
            }
        })();

        const imageUrl = await Promise.race([request, timeout]);
        if (timeoutId) clearTimeout(timeoutId);
        return finish(imageUrl);
    };
}
